// Thin createServerFn wrappers for trauma purchases. Logic lives in purchase.server.ts.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

async function viewer() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { isTraumaEnabled } = await import("@/lib/trauma/gate.server");
  const { requestIsAdmin } = await import("@/lib/bousala/smart-preview.server");
  const { requestUserId } = await import("@/lib/trauma/request-user.server");
  const { resolveAccountEmailCore } = await import("@/lib/bousala-purchase.functions");
  const [enabled, isAdmin, userId] = await Promise.all([isTraumaEnabled(), requestIsAdmin(), requestUserId()]);
  const core = await import("@/lib/trauma/purchase.server");
  return {
    core,
    v: {
      admin: supabaseAdmin,
      canView: enabled || isAdmin,
      isAdmin,
      isEnabled: isTraumaEnabled,
      userId,
      resolveEmail: (id: string) => resolveAccountEmailCore(supabaseAdmin, id),
      paymentsToken: process.env.VITE_PAYMENTS_CLIENT_TOKEN,
    },
  };
}

export const getTraumaBuyContext = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ productSlug: z.string().max(80) }).parse(d))
  .handler(async ({ data }) => {
    const { core, v } = await viewer();
    return core.getTraumaBuyContextCore(v, data);
  });

export const getTraumaPurchasable = createServerFn({ method: "GET" }).handler(async () => {
  const { core, v } = await viewer();
  return core.getTraumaPurchasableCore(v);
});

function safeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-80);
}

export const getTraumaReceiptUploadUrl = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ filename: z.string().min(1).max(160) }).parse(d))
  .handler(async ({ data }) => {
    const { core, v } = await viewer();
    if (!v.canView) throw new Error(core.NOT_AVAILABLE);
    const path = `trauma/pending/${crypto.randomUUID()}-${safeFilename(data.filename)}`;
    const { data: signed, error } = await v.admin.storage.from("payment-receipts").createSignedUploadUrl(path);
    if (error) throw error;
    return { path: signed.path, token: signed.token };
  });

const submitInput = z.object({
  productSlug: z.string().max(80),
  email: z.string().email().max(200),
  full_name: z.string().min(2).max(120),
  phone: z.string().min(6).max(40),
  country: z.string().length(2),
  receipt_path: z.string().min(1).max(400),
});

export const submitTraumaPurchase = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => submitInput.parse(d))
  .handler(async ({ data }) => {
    const { core, v } = await viewer();
    return core.submitTraumaPurchaseCore(v, data);
  });
